import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl, intlShape } from 'react-intl';
import { Alert, Button, Col, Row } from 'reactstrap';
import { Link } from 'react-router-dom';
import { get, filter } from 'lodash';
import { Helmet } from 'react-helmet-async';
import { modelOf } from '../../../../prop-types';
import ContentForState from '../../../loader/ContentForState';
import PageTitle from '../../../common/PageTitle';
import SEOTitle from '../../../head/SEOTitle';
import HelperNavigation from '../../../common/HelperNavigation';
import globalTranslations from '../../../../i18n/globalTranslations';
import Paths from '../../../../types/Paths';
import RouteService from '../../../../services/RouteService';
import CustomerUserStore from '../../../../store/CustomerUserStore';
import LanguageStore from '../../../../store/LanguageStore';
import RequestState from '../../../../types/RequestState';
import Icon from '../../../common/Icon';
import BalanceUserList from '../BalanceUserList';
import UnexpectedError from '../../../loader/UnexpectedError';

const KLARNA_CHECKOUT_TIMEOUT_MS = 1200;

@observer
export class BalanceUserAccountOverview extends Component {
  state = {
    listState: RequestState.NONE,
    paymentAmount: [],
    paymentInProgress: false,
    showBalance: false,
    removeError: null,
    requestId: 0,
    quantityErrors: [],
  };

  timeout;

  componentDidMount() {
    this.setState({
      listState: RequestState.LOADING,
    });
    this.maybeLoadBalanceUsers();
  }

  componentWillUnmount() {
    this.resetTimeout();
  }

  resetTimeout = () => {
    this.timeout && clearTimeout(this.timeout);
  };

  maybeLoadBalanceUsers() {
    const { customerUserStore } = this.props;
    customerUserStore
      .loadBalanceUsers()
      .then(() => {
        this.setState({
          listState: RequestState.LOADED,
        });
      })
      .catch(() => {
        this.setState({
          listState: RequestState.ERROR,
        });
      });
  }

  getBreadcrumbsPath = () => {
    const { intl, routeService } = this.props;
    return routeService.transformBreadCrumbs([
      {
        text: intl.formatMessage(globalTranslations.myAccount),
        url: Paths.MyAccountBalanceUser,
      },
    ]);
  };

  getBalanceUserButtonRow() {
    const { routeService } = this.props;
    return (
      <Row className="BalanceUserAccountOverview__balance-user-button-row">
        <Col size="12">
          <Link to={routeService.getPath(Paths.MyAccountUserNew)}>
            <Button
              color="primary"
              className="BalanceUserAccountOverview__add-new-user-button"
              size="lg"
            >
              <FormattedMessage {...globalTranslations.addNewUserTitle} />
            </Button>
          </Link>
          <Button
            color="success"
            disabled={this.state.paymentInProgress}
            onClick={this.handleBalance}
            size="lg"
            className="BalanceUserAccountOverview__add-balance-button"
          >
            <Icon name="shopping-cart" />
            <span className="BalanceUserAccountOverview__add-balance-button-text">
              <FormattedMessage
                id="checkout.buyBalanceSentence"
                defaultMessage="Buy balance"
              />
            </span>
          </Button>
          {(this.state.showBalance || this.state.paymentInProgress) && (
            <Button
              color="secondary"
              size="lg"
              className="BalanceUserAccountOverview__cancel-payment-button"
              onClick={this.cancelPayment}
            >
              <span className="BalanceUserAccountOverview__cancel-payment-button-text">
                <FormattedMessage
                  id="checkout.cancelPayment"
                  defaultMessage="Cancel"
                />
              </span>
            </Button>
          )}
        </Col>
      </Row>
    );
  }

  handleBalance = () => {
    this.setState((prevState) => ({ showBalance: !prevState.showBalance }));
  };

  cancelPayment = () => {
    this.setState({
      showBalance: false,
      paymentInProgress: false,
      paymentAmount: [],
    });
  };

  showBuyBalanceInformationBanner = () => {
    if (!this.state.showBalance) {
      return null;
    }

    return (
      <Alert
        className="BalanceUserAccountOverview__buy-balance-info-alert"
        color="light"
      >
        <FormattedMessage
          id="myAccount.buyBalanceInfoText"
          defaultMessage="Select an amount for the users you want to transfer money to and wait a moment."
        />
      </Alert>
    );
  };

  renderError = () => {
    const { requestId, removeError } = this.state;
    const errors = get(removeError, 'response.data.errors');
    return errors ? (
      <Alert className="MyAccountUser__errors" color="danger" key={requestId}>
        {Object.values(removeError.response.data.errors).map((message) => (
          <div key={message}>{message}</div>
        ))}
      </Alert>
    ) : (
      <UnexpectedError />
    );
  };

  renderBalanceUserList = () => (
    <BalanceUserList
      showBalance={this.state.showBalance}
      onPayment={() => null}
      onBalanceUpdate={this.onBalanceUpdate}
      onRemoveUser={this.removeUser}
      totalSums={this.state.paymentAmount}
      onBalanceError={this.onBalanceError}
    />
  );

  onBalanceUpdate = (id, value, user) => {
    this.suspendKlarnaCheckout();
    const newAmounts = this.updateBalance(id, value, user);
    this.updatePaymentAmounts(newAmounts);
  };

  onBalanceError = (id, quantityField) => {
    const quantityErrors = quantityField.errors;
    if (quantityErrors) {
      this.setState({ quantityErrors });
    }
  };
  suspendKlarnaCheckout() {
    if (this.state.paymentAmount.length > 0 && window._klarnaCheckout) {
      window._klarnaCheckout(function (api) {
        api.suspend();
      });
    }
  }

  updateBalance = (id, value, user) => {
    const newAmounts = this.state.paymentAmount;

    if (
      filter(newAmounts, (amount) => amount.id === id).length === 0 &&
      value !== 0
    ) {
      newAmounts.push({
        id: id,
        amount: value,
        sum: user.account_balance + value,
      });
    } else {
      newAmounts.forEach((amount, i) => {
        if (amount.id === id) {
          amount.amount = value;
          amount.sum = user.account_balance + value;
        }

        if (amount.amount === 0) {
          newAmounts.splice(i, 1);
        }
      });
    }
    return newAmounts;
  };

  updatePaymentAmounts = (paymentAmount) => {
    this.setState(
      {
        paymentAmount,
      },
      () => {
        const { paymentAmount } = this.state;
        if (paymentAmount.length === 0) {
          this.resetPaymentTransaction();
        }

        if (paymentAmount.length > 0) {
          this.resetTimeout();
          this.timeout = setTimeout(() => {
            this.handlePayment(paymentAmount);
          }, KLARNA_CHECKOUT_TIMEOUT_MS);
        }
      }
    );
  };

  resetPaymentTransaction = () => {
    this.setState({
      paymentInProgress: false,
      paymentAmount: [],
    });
    this.resetTimeout();
  };

  handlePayment = (paymentAmounts) => {
    const { customerUserStore, languageStore } = this.props;
    const { showBalance, quantityErrors } = this.state;
    const userPayments =
      customerUserStore.convertPaymentAmountsIntoUserPayments(paymentAmounts);

    if (showBalance && paymentAmounts && quantityErrors.length === 0) {
      customerUserStore
        .makePayment(userPayments, languageStore.locale)
        .then(() => {
          if (paymentAmounts.length > 0) {
            this.setState(
              {
                paymentInProgress: true,
              },
              () => {
                this.resumeKlarnaCheckout();
                this.resetTimeout();
              }
            );
          }
        })
        .catch(() => {
          this.setState({
            paymentInProgress: false,
          });
          this.resetTimeout();
        });
    }
  };

  resumeKlarnaCheckout() {
    if (this.state.paymentAmount.length > 0 && window._klarnaCheckout) {
      window._klarnaCheckout(function (api) {
        api.resume();
      });
    }
  }

  removeUser = (id) => {
    const { customerUserStore } = this.props;
    customerUserStore
      .removeBalanceUser(id)
      .then(() => {
        customerUserStore.loadBalanceUsers();
        this.setState({ removeError: null });
      })
      .catch((error) =>
        this.setState({
          removeError: error,
          requestId: this.state.requestId + 1,
        })
      );
  };

  renderHtmlWidget = (widgetHtml) => {
    if (!widgetHtml) {
      return this.paymentWidgetErrorBanner();
    }

    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = widgetHtml;

    return (
      <Fragment>
        <Helmet>
          {[].map.call(doc.getElementsByTagName('script'), (el, i) => (
            <script key={i}>{el.textContent}</script>
          ))}
        </Helmet>
        <Row className="BalanceUserAccountOverview__klarna-wrapper-container">
          <Col
            className="BalanceUserAccountOverview__klarna-container-header"
            sm={12}
          >
            <PageTitle>
              <FormattedMessage
                id="myAccount.klarnaCheckoutPaymentInfo"
                defaultMessage="Please give your payment info."
              />
            </PageTitle>
          </Col>
          <Col className="BalanceUserAccountOverview__klarna-container">
            <div dangerouslySetInnerHTML={{ __html: widgetHtml }} />
          </Col>
        </Row>
      </Fragment>
    );
  };

  paymentWidgetErrorBanner = () => (
    <Alert
      className="BalanceUserAccountOverview__payment-widget-error-banner"
      color="danger"
    >
      <FormattedMessage
        {...globalTranslations.balanceUserAccountPaymentWidgetError}
      />
    </Alert>
  );

  render() {
    const { customerUserStore } = this.props;
    const { removeError } = this.state;
    const inPayment =
      this.state.paymentInProgress && customerUserStore.balancePayment !== null;

    return (
      <div className="BalanceUserAccountOverview">
        <HelperNavigation breadcrumbsPath={this.getBreadcrumbsPath()} />
        <SEOTitle title={globalTranslations.myAccount} />
        <PageTitle>
          <FormattedMessage {...globalTranslations.myAccount} />
        </PageTitle>
        {this.getBalanceUserButtonRow()}
        {this.showBuyBalanceInformationBanner()}
        {removeError && this.renderError()}
        <ContentForState
          state={customerUserStore.state}
          error={customerUserStore.lastError}
          forLoaded={() => this.renderBalanceUserList()}
        />
        {inPayment &&
          customerUserStore &&
          this.renderHtmlWidget(customerUserStore.balancePayment.html_widget)}
      </div>
    );
  }
}

BalanceUserAccountOverview.propTypes = {
  customerUserStore: modelOf(CustomerUserStore).isRequired,
  languageStore: modelOf(LanguageStore).isRequired,
  intl: intlShape.isRequired,
  routeService: PropTypes.instanceOf(RouteService).isRequired,
};

export default injectIntl(
  inject(
    'customerUserStore',
    'languageStore',
    'routeService'
  )(BalanceUserAccountOverview)
);
